/**
 * Chips dos 14 alergénios da UE e selo de estado (#325). Partilhados pelo
 * editor do produto (Menu) e pela revisão do import, para os dois ecrãs
 * terem exatamente os mesmos chips, marcas de origem e estados.
 */
import {
  ALLERGEN_BADGE,
  ALLERGEN_BADGE_CLASS,
  EU_ALLERGEN_CODES,
  allergenBadgeText,
  allergenLabel,
  type AllergenBadgeState,
} from '../lib/allergens.js'
import { Badge } from './ui/index.js'
import { cn } from '../lib/utils.js'
import { useI18n } from '../lib/i18n.js'

/**
 * Selo dos alergénios com os 4 estados (por rever, por confirmar, sem
 * alergénios, confirmados) — o MESMO componente no cartão do catálogo, no
 * editor do produto e na revisão do import (QA da #340). O texto distingue o
 * estado (não só a cor). Não é botão, para não aninhar botões.
 */
export function AllergenBadge({
  state,
  testId,
  prefix = true,
}: {
  state: AllergenBadgeState
  testId: string
  /** false: o selo está ao lado do rótulo «Alergénios» (editor, revisão do
   * import) — mostra só o estado; o title mantém o texto completo */
  prefix?: boolean
}) {
  const { t } = useI18n()
  const { color, icon } = ALLERGEN_BADGE[state]
  const text = allergenBadgeText(t, state, { prefix })
  return (
    <span data-testid={testId} data-state={state} title={allergenBadgeText(t, state)}>
      <Badge color={color} className={ALLERGEN_BADGE_CLASS}>
        <span aria-hidden="true" className="font-bold" data-testid={`${testId}-icon`}>
          {icon}
        </span>
        <span>{text}</span>
      </Badge>
    </span>
  )
}

export function AllergenChips({
  selected,
  sourceOf,
  onToggle,
  testIdPrefix,
}: {
  selected: number[]
  /** origem da sugestão do código, para a marca ▣ (impresso) / ✦ (IA) */
  sourceOf: (code: number) => 'menu' | 'ai' | undefined
  onToggle: (code: number) => void
  /** prefixo do data-testid; cada chip fica `${prefixo}${código}` */
  testIdPrefix: string
}) {
  const { t, lang } = useI18n()
  return (
    <div className="flex flex-wrap gap-2">
      {EU_ALLERGEN_CODES.map((code) => {
        const on = selected.includes(code)
        const sug = sourceOf(code)
        const origin = sug
          ? sug === 'menu'
            ? t('admin.menu.allergens_from_menu')
            : t('admin.menu.allergens_from_ai')
          : undefined
        return (
          <button
            key={code}
            type="button"
            data-testid={`${testIdPrefix}${code}`}
            data-suggested={sug}
            aria-pressed={on}
            title={origin}
            onClick={() => onToggle(code)}
            className={cn(
              'inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs transition-colors',
              on
                ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300',
              sug && 'border-dashed',
            )}
          >
            <span className="tabular-nums text-[10px] opacity-60">{code}</span>
            {allergenLabel(t, code, lang)}
            {sug && (
              <span aria-label={origin} className="text-[10px]">
                {sug === 'menu' ? '▣' : '✦'}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

/** Legenda das marcas de origem. */
export function AllergenLegend() {
  const { t } = useI18n()
  return (
    <p className="text-[11px] text-gray-400">
      ✦ {t('admin.menu.allergens_from_ai')} · ▣ {t('admin.menu.allergens_from_menu')}
    </p>
  )
}
